import map from 'lodash/map';
import pick from 'lodash/pick';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router';
import classnames from 'classnames';
import { Column, Table as VirtualizedTable, WindowScroller } from 'react-virtualized';
import 'react-virtualized/styles.css';

import Checkbox from '@mui/material/Checkbox';
import Drawer from '@mui/material/Drawer';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { TableCell as MuiTableCell } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Toolbar from '@mui/material/Toolbar';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import Chip from '@mui/material/Chip';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import ArchiveIcon from '@mui/icons-material/Archive';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Avatar from '@mui/material/Avatar';
import ClickToCopyButton from 'widgets/click-to-copy-button';
import { can } from 'services/can';
import { PrimaryButton } from 'widgets-v5/buttons';
import { BodyText } from 'widgets-v5/typography';
import { DialogTitle } from 'widgets-v6/modal';
import { SummaryLayout, SummaryGroup } from 'widgets/summary-view/summary-layout';
import SummaryTypeText from 'widgets/summary-view/summary-type-text';
import SummaryTypeComponent from 'widgets/summary-view/summary-type-component';
import SummaryTypeTextCollection from 'widgets/summary-view/summary-type-text-collection';
import MiddleTruncatedText from 'widgets-v6/middle-truncated-text';
import { vendorToPresentationMapping } from 'common/constants/vendors';

import {
    platformsMapping,
    typesMapping,
    formatsMapping,
    placementsMapping,
    positionsMapping,
    sizesMapping,
    creativeTrackingVendorsMapping,
} from 'forms/creative-form/constants';
import {
    openCreativeWarningModal,
    closeCreativeWarningModal,
} from '../creative-bulk-edit-audit-warning/actions';
import { CreativeBulkEdit } from 'forms/creative-bulk-edit-form/actions';
import { canEditCreative, canArchiveCreative } from 'states/resources/creatives/business-logic';
import CreativeBulkEditForm from 'forms/creative-bulk-edit-form';
import CreativeBulkUploadForm from 'forms/creative-bulk-upload-form';
import CreativeAuditWarning from '../creative-bulk-edit-audit-warning';
import CreativeBulkArchiveConfirmModal from '../creative-bulk-archive';
import CreativeActionsMenu from '../creative-actions-menu';
import flags from 'containers/flags/service';
import { orange } from '@mui/material/colors';
import {
    CampaignTypeMapping,
    isCampaignLockedOrArchived,
} from 'states/resources/campaigns/business-logic';
import { getEnvironmentSettings } from 'services/environment';
import { PermissionsMapping } from 'common/constants/role-intents';

const useStyles = makeStyles({
    image: {
        maxWidth: '250px',
    },
    drawer: {
        width: '60%',
        padding: '10px 30px',
    },
    creativeMainDetails: {
        margin: '15px 0px 20px',
    },
    creativeDetails: {
        wordBreak: 'break-all',
        margin: '10px 0px',
    },
    link: {
        background: '#f9f9f9',
        padding: 10,
        font:
            "12px/normal 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace",
        lineHeight: '18px',
        marginBottom: 10,
        wordBreak: 'break-all',
    },
    tableCell: {
        border: 'none',
        fontWeight: 'initial',
        textTransform: 'none',
    },
});

const chipStyles = makeStyles(theme => ({
    root: ({ status }) => ({
        backgroundColor: status !== 'approved' ? orange[300] : theme.palette.primary[500],
        fontWeight: theme.typography.fontWeightBold,
        color: status !== 'approved' ? theme.palette.common.black : theme.palette.common.white,
        width: '72px',
    }),
}));

const TableCell = props => {
    const classes = useStyles();
    return <MuiTableCell classes={{ root: classes.tableCell }}>{props.children}</MuiTableCell>;
};

const AUDIT_LABELS = {
    no_audit: 'Draft',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
};

const AuditChip = ({ status }) => {
    const classes = chipStyles({ status });
    return <Chip label={AUDIT_LABELS[status]} classes={{ root: classes.root }} />;
};

function AdsPopover(props) {
    const { ads, open, anchor, onClose, campaignId } = props;

    return (
        <Popover
            id="mouse-over-popover"
            open={open}
            anchorEl={anchor}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            onClose={onClose}
            disableRestoreFocus
        >
            <List>
                {map(ads, ad => (
                    <Link
                        key={ad.id}
                        color="initial"
                        to={`/campaigns/${campaignId}/setup/ads#${ad.id}`}
                        component={MuiLink}
                    >
                        <ListItem button>
                            <Typography variant="body2">{ad.name}</Typography>
                        </ListItem>
                    </Link>
                ))}
            </List>
        </Popover>
    );
}

const VirtualizedCreativesTable = ({
    dispatch,
    selectedRows,
    campaignId,
    creatives,
    campaign,
    classes,
    handleCheckAll,
    handleCheckRow,
    editCreative,
    openCreativeHistory,
    duplicateCreative,
    setPermalink,
    renderPlatformLabel,
    submitCreativeEditFormDraft,
    openArchiveConfirmModal,
    openBulkArchiveConfirmModal,
    shouldShowArchivedCreatives,
    toggleArchivedAdVisibility,
}) => {
    const scroll = useRef();
    const isDOOHCampaign = campaign.type === CampaignTypeMapping.DOOH;
    const useAspectRatio = flags.isEnabled('efb_63_dooh_creative_resizing') && isDOOHCampaign;

    const [currentCreativeId, setCurrentCreativeId] = React.useState(null);
    const [anchorElAds, setAnchorElAds] = React.useState(null);
    const openAds = Boolean(anchorElAds);

    const handlePopoverOpen = ({ event, creativeId }) => {
        setCurrentCreativeId(creativeId);
        setAnchorElAds(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorElAds(null);
    };

    useEffect(() => {
        scroll.current = document.getElementById('creative-overview-workspace-scroll-container');
    }, []);

    const closeCreativeAuditWarning = () => {
        dispatch(closeCreativeWarningModal());
    };

    const openCreativeBulkEditForm = () => {
        closeCreativeAuditWarning();
        dispatch(CreativeBulkEdit.openForm(campaignId));
    };

    const openCreativeAuditWarning = () => {
        dispatch(openCreativeWarningModal());
    };

    const checkAuditStatus = () => {
        const selectedCreatives = filter(creatives, creative => selectedRows.includes(creative.id));
        const approvedCreatives = filter(
            selectedCreatives,
            ({ audit_status }) => audit_status === 'pending' || audit_status === 'approved'
        );
        if (approvedCreatives.length > 0) {
            openCreativeAuditWarning();
        } else {
            openCreativeBulkEditForm();
        }
    };

    const getRowHeight = ({ index }) => {
        let rowHeight = 60 + 15 * creatives[index].connectedAds.length;
        return rowHeight;
    };

    const rowGetter = ({ index }) => {
        const creative = creatives[index];
        const creativeForExpanded = pick(creative, [
            'placements',
            'positions',
            'image_url',
            'image_preview_url',
            'content_html',
            'companion_url',
            'companion_preview_url',
            'clickthrough_url',
            'landing_page',
            'third_party_quartile_pixels',
            'reference_image_preview_url',
            'third_party_vendors',
            'third_party_pixels',
            'third_party_javascript_urls',
            'has_screenshot',
        ]);
        return {
            creativeForExpanded,
            creative,
        };
    };

    const headerRenderer = ({ label, dataKey }) => {
        switch (dataKey) {
            case 'check':
                return (
                    <TableCell
                        className="ReactVirtualized__Table__headerRow"
                        padding="checkbox"
                        component="div"
                    >
                        {canEditCreative(campaign) && (
                            <Tooltip
                                title={
                                    isCampaignLockedOrArchived(campaign)
                                        ? 'Edit not enabled when campaign is locked/archived'
                                        : null
                                }
                            >
                                <span>
                                    <Checkbox
                                        onClick={handleCheckAll}
                                        checked={selectedRows.length === creatives.length}
                                        color="primary"
                                        disabled={isCampaignLockedOrArchived(campaign)}
                                    />
                                </span>
                            </Tooltip>
                        )}
                    </TableCell>
                );
            case 'audit':
                return (
                    <TableCell align="center" component="div">
                        {' '}
                        Audit Status{' '}
                    </TableCell>
                );
            case 'expand':
                return <TableCell />;

            default:
                return <TableCell component="div"> {label} </TableCell>;
        }
    };

    const checkCellRenderer = ({ cellData, style }) => {
        const id = cellData;

        return (
            <TableCell padding="checkbox" style={style} component="div">
                {canEditCreative(campaign) && (
                    <Tooltip
                        title={
                            isCampaignLockedOrArchived(campaign)
                                ? 'Edit not enabled when campaign is locked/archived'
                                : null
                        }
                    >
                        <span>
                            <Checkbox
                                onClick={() => handleCheckRow(id)}
                                checked={includes(selectedRows, id)}
                                color="primary"
                                disabled={isCampaignLockedOrArchived(campaign)}
                            />
                        </span>
                    </Tooltip>
                )}
            </TableCell>
        );
    };

    const idCellRenderer = ({ cellData, style }) => {
        const id = cellData;
        return (
            <TableCell style={style} component="div">
                {id}
            </TableCell>
        );
    };
    const nameCellRenderer = ({ rowData, style }) => {
        const { creative } = rowData;
        const { id, name, connectedAds } = creative;

        return (
            <TableCell style={style} component="div">
                <div className={classes.nameContainer}>
                    {canEditCreative(campaign) ? (
                        <Link to={`/campaigns/${campaignId}/setup/creatives/${id}/edit`}>
                            <BodyText type="link">{name}</BodyText>
                        </Link>
                    ) : (
                        <Typography gutterBottom>{name}</Typography>
                    )}
                </div>
                {flags.isEnabled('en_2113_creative_list_add_pills_for_ads') ? (
                    <Box mt={1}>
                        {connectedAds.length > 0 && (
                            <Chip
                                avatar={<Avatar>{connectedAds.length}</Avatar>}
                                label="Ads"
                                onClick={e => handlePopoverOpen({ event: e, creativeId: id })}
                                size="small"
                            />
                        )}
                        {currentCreativeId === id && (
                            <AdsPopover
                                ads={connectedAds}
                                open={openAds}
                                anchor={anchorElAds}
                                onClose={handlePopoverClose}
                                campaignId={campaignId}
                            />
                        )}
                    </Box>
                ) : (
                    <div style={{ maxWidth: '100%' }}>
                        Ads:
                        <br />
                        {connectedAds.length !== 0 ? (
                            map(connectedAds, ad => (
                                <Tooltip
                                    title={`#${ad.id} : ${ad.name}`}
                                    classes={{ tooltip: classes.tooltip }}
                                >
                                    <MuiLink
                                        key={ad.id}
                                        to={`/campaigns/${campaignId}/setup/ads#${ad.id}`}
                                    >
                                        <div
                                            style={{ maxWidth: '100%' }}
                                            className="ef3-campaignOverview_CreativeList_connectedAds"
                                        >
                                            <MiddleTruncatedText text={`#${ad.id} : ${ad.name}`} />
                                        </div>
                                    </MuiLink>
                                </Tooltip>
                            ))
                        ) : (
                            <div>No connected ads</div>
                        )}
                    </div>
                )}
            </TableCell>
        );
    };

    const actionsCellRenderer = ({ rowData }) => {
        const { creative } = rowData;
        return (
            <CreativeActionsMenu
                campaign={campaign}
                campaignId={campaignId}
                creative={creative}
                creativeId={creative.id}
                editCreative={editCreative}
                openCreativeHistory={openCreativeHistory}
                duplicateCreative={duplicateCreative}
                openArchiveConfirmModal={openArchiveConfirmModal}
                setPermalink={setPermalink}
                creativeFormat={creative.format}
            />
        );
    };

    const previewCellRenderer = ({ rowData, style }) => {
        const { creative } = rowData;
        const { format } = rowData.creative;
        const useJobForBulkCreativeScreenshots = getEnvironmentSettings().useJobForBulkCreativeScreenshots();

        return (
            <TableCell style={style} component="div">
                {(() => {
                    if (useJobForBulkCreativeScreenshots && !creative.has_screenshot) {
                        return (
                            <Chip
                                label="In progress"
                                title="This screenshot will be auto-generated in background"
                            />
                        );
                    }

                    switch (format) {
                        case 'custom_html':
                        case 'mraid':
                            return (
                                <img
                                    src={creative.reference_image_preview_url}
                                    className={classes.image}
                                />
                            );
                        case 'native':
                            return (
                                <img
                                    src={creative.main_image_preview_url}
                                    className={classes.image}
                                />
                            );
                        default:
                            return (
                                <img src={creative.image_preview_url} className={classes.image} />
                            );
                    }
                })()}
            </TableCell>
        );
    };
    const summaryCellRenderer = ({ rowData, style, rowIndex }) => {
        const { creative } = rowData;
        return (
            <TableCell style={style} component="div">
                <Summary
                    creative={creative}
                    rowIndex={rowIndex}
                    rowData={rowData}
                    isDOOHCampaign={isDOOHCampaign}
                    useAspectRatio={useAspectRatio}
                />
            </TableCell>
        );
    };

    const platformCellRenderer = ({ rowData, style }) => {
        const { creative } = rowData;
        return (
            <TableCell style={style} component="div">
                {renderPlatformLabel(creative.platforms)}
            </TableCell>
        );
    };

    const formatCellRenderer = ({ rowData, style }) => {
        const { creative } = rowData;
        return (
            <TableCell style={style} component="div">
                {typesMapping[creative.type]}
            </TableCell>
        );
    };

    const typeCellRenderer = ({ rowData, style }) => {
        const { format } = rowData.creative;
        return (
            <TableCell style={style} component="div">
                {formatsMapping[format]}
            </TableCell>
        );
    };
    const sizeCellRenderer = ({ rowData, style }) => {
        const { size, aspect_ratio } = rowData.creative;
        const sizeOrRatio =
            flags.isEnabled('efb_63_dooh_creative_resizing') && aspect_ratio ? aspect_ratio : size;
        return (
            <TableCell style={style} component="div">
                {size === '9999x9999' ? 'Responsive' : sizeOrRatio}
            </TableCell>
        );
    };

    const auditCellRenderer = ({ rowData, style }) => {
        const { creative } = rowData;
        const { audit_status } = creative;

        return (
            <TableCell align="center" style={style} component="div">
                <div className={classes.auditContainer}>
                    {' '}
                    <AuditChip status={audit_status} /> <br />
                    {can(PermissionsMapping.CAMPAIGN__VIEW_AND_EDIT) &&
                        audit_status === 'no_audit' && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => submitCreativeEditFormDraft(creative)}
                            >
                                Submit
                            </Button>
                        )}
                </div>
            </TableCell>
        );
    };

    return (
        <Paper classes={{ root: classes.paper }}>
            <div className="ef3-campaignOverview_CreativeList_WorkspaceToolbar">
                <AppBar position="static" color="inherit" elevation={0}>
                    <Toolbar
                        classes={{ root: classes.toolbar }}
                        className={classnames({
                            [classes.highlight]: selectedRows.length,
                        })}
                    >
                        {selectedRows.length ? (
                            <React.Fragment>
                                <div>
                                    <Typography variant="subtitle1">
                                        {' '}
                                        {`${selectedRows.length} selected`}{' '}
                                    </Typography>
                                </div>
                                <div>
                                    {canEditCreative(campaign) && (
                                        <Tooltip
                                            title="Bulk Edit"
                                            classes={{ root: classes.tooltip }}
                                        >
                                            <IconButton onClick={checkAuditStatus} size="large">
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {canArchiveCreative(campaign) && (
                                        <Tooltip title="Bulk Archive">
                                            <IconButton
                                                onClick={() =>
                                                    openBulkArchiveConfirmModal(selectedRows)
                                                }
                                                size="large"
                                            >
                                                <ArchiveIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <Typography variant="h6"> All Creatives </Typography>

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={shouldShowArchivedCreatives}
                                            onChange={toggleArchivedAdVisibility}
                                            color="primary"
                                        />
                                    }
                                    label="Show Archived Creatives"
                                />
                            </React.Fragment>
                        )}
                    </Toolbar>
                </AppBar>
            </div>
            <div className="creatives-overview__stackable-table">
                {scroll.current && (
                    <WindowScroller scrollElement={scroll.current}>
                        {({
                            width = 1000,
                            height = 500,
                            isScrolling,
                            onChildScroll,
                            scrollTop,
                        }) => (
                            <VirtualizedTable
                                headerHeight={60}
                                height={height}
                                width={width}
                                rowHeight={getRowHeight}
                                rowCount={creatives.length}
                                rowGetter={rowGetter}
                                isScrolling={isScrolling}
                                scrollTop={scrollTop}
                                onScroll={onChildScroll}
                                autoHeight
                            >
                                <Column
                                    label="Check"
                                    dataKey="check"
                                    width={width * 0.1}
                                    headerRenderer={({ dataKey, label }) =>
                                        headerRenderer({ dataKey, label })
                                    }
                                    cellDataGetter={({ rowData }) => rowData.creative.id}
                                    cellRenderer={checkCellRenderer}
                                />
                                <Column
                                    label="ID"
                                    dataKey="id"
                                    width={width * 0.15}
                                    headerRenderer={({ dataKey, label }) =>
                                        headerRenderer({ dataKey, label })
                                    }
                                    cellDataGetter={({ rowData }) => rowData.creative.id}
                                    cellRenderer={idCellRenderer}
                                />
                                <Column
                                    label="Name"
                                    dataKey="name"
                                    width={width * 0.6}
                                    headerRenderer={({ dataKey, label }) =>
                                        headerRenderer({ dataKey, label })
                                    }
                                    cellDataGetter={({ rowData }) => rowData.creative.name}
                                    cellRenderer={nameCellRenderer}
                                />
                                <Column
                                    label="Summary"
                                    dataKey="summary"
                                    width={width * 0.1}
                                    cellDataGetter={({ rowData }) => rowData.creative.format}
                                    headerRenderer={({ dataKey, label }) =>
                                        headerRenderer({ dataKey, label })
                                    }
                                    cellRenderer={summaryCellRenderer}
                                />
                                <Column
                                    label="Actions"
                                    dataKey="actions"
                                    width={width * 0.15}
                                    headerRenderer={({ dataKey, label }) =>
                                        headerRenderer({ dataKey, label })
                                    }
                                    cellDataGetter={({ rowData }) => rowData.creative.actions}
                                    cellRenderer={actionsCellRenderer}
                                />
                                <Column
                                    label="Preview"
                                    dataKey="preview"
                                    width={width * 0.4}
                                    cellDataGetter={({ rowData }) => rowData.creative.format}
                                    headerRenderer={({ dataKey, label }) =>
                                        headerRenderer({ dataKey, label })
                                    }
                                    cellRenderer={previewCellRenderer}
                                />
                                <Column
                                    label="Platforms"
                                    dataKey="platforms"
                                    width={width * 0.15}
                                    cellDataGetter={({ rowData }) => rowData.creative.format}
                                    headerRenderer={({ dataKey, label }) =>
                                        headerRenderer({ dataKey, label })
                                    }
                                    cellRenderer={platformCellRenderer}
                                />
                                <Column
                                    label="Format"
                                    dataKey="format"
                                    width={width * 0.15}
                                    headerRenderer={({ dataKey, label }) =>
                                        headerRenderer({ dataKey, label })
                                    }
                                    cellDataGetter={({ rowData }) => rowData.creative.format}
                                    cellRenderer={formatCellRenderer}
                                />
                                <Column
                                    label="Type"
                                    dataKey="type"
                                    width={width * 0.1}
                                    headerRenderer={({ dataKey, label }) =>
                                        headerRenderer({ dataKey, label })
                                    }
                                    cellDataGetter={({ rowData }) => rowData.creative.type}
                                    cellRenderer={typeCellRenderer}
                                />
                                <Column
                                    label={useAspectRatio ? 'Aspect Ratio' : 'Size'}
                                    dataKey="size"
                                    width={width * 0.15}
                                    headerRenderer={({ dataKey, label }) =>
                                        headerRenderer({ dataKey, label })
                                    }
                                    cellDataGetter={({ rowData }) => rowData.creative.size}
                                    cellRenderer={sizeCellRenderer}
                                />
                                <Column
                                    label="Audit Status"
                                    dataKey="audit"
                                    width={width * 0.25}
                                    headerRenderer={({ dataKey, label }) =>
                                        headerRenderer({ dataKey, label })
                                    }
                                    cellDataGetter={({ rowData }) => rowData.creative.size}
                                    cellRenderer={auditCellRenderer}
                                />
                            </VirtualizedTable>
                        )}
                    </WindowScroller>
                )}
            </div>
            <CreativeBulkUploadForm campaignId={campaignId} campaignType={campaign.type} />
            <CreativeBulkEditForm campaignId={campaignId} selectedCreativeIds={selectedRows} />
            <CreativeAuditWarning
                onClose={closeCreativeAuditWarning}
                creatives={creatives}
                selectedCreativeIds={selectedRows}
                openCreativeBulkEditForm={openCreativeBulkEditForm}
            />
            <CreativeBulkArchiveConfirmModal campaignId={campaignId} selectedRows={selectedRows} />
        </Paper>
    );
};

function Summary(props) {
    const classes = useStyles();

    const { creative, rowData, isDOOHCampaign, useAspectRatio } = props;

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClick = event => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        const hashId = window.location.hash.slice(1);
        if ('' !== hashId && props.creative.id === hashId) {
            setAnchorEl(hashId);
        }
    }, [window.location.hash]);

    const open = Boolean(anchorEl);

    return (
        <div>
            <IconButton onClick={handleClick} size="large">
                <AssignmentIcon />
            </IconButton>
            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                classes={{ paper: classes.drawer }}
            >
                <DialogTitle onClose={handleClose}>{`#${creative.id} ${
                    creative.name
                }`}</DialogTitle>
                <CreativeSummary
                    creative={rowData.creative}
                    isDOOHCampaign={isDOOHCampaign}
                    useAspectRatio={useAspectRatio}
                />
            </Drawer>
        </div>
    );
}

function StyledLink(props) {
    const classes = useStyles();
    return <div className={classes.link}>{props.children}</div>;
}

function CreativeSummary(props) {
    const classes = useStyles();
    const { creative, isDOOHCampaign, useAspectRatio } = props;

    let creativeAsset;

    switch (creative.format) {
        case 'custom_html':
        case 'mraid':
            creativeAsset = (
                <img src={creative.reference_image_preview_url} className={classes.image} />
            );
        case 'native':
            creativeAsset = <img src={creative.main_image_preview_url} className={classes.image} />;
        default:
            creativeAsset = <img src={creative.image_preview_url} className={classes.image} />;
    }

    return (
        <SummaryLayout title="Summary">
            <SummaryGroup groupName="Basic">
                <SummaryTypeText keyName="Name" value={creative.name} />
                <SummaryTypeText
                    keyName="Audit Status"
                    value={AUDIT_LABELS[creative.audit_status]}
                />
                <SummaryTypeTextCollection
                    keyName="Platforms"
                    values={map(creative.platforms, platform => platformsMapping[platform])}
                />
                <SummaryTypeText keyName="Format" value={typesMapping[creative.type]} />
                {!isDOOHCampaign && creative.placements && (
                    <SummaryTypeTextCollection
                        keyName="Placements"
                        values={map(creative.placements, placement => placementsMapping[placement])}
                    />
                )}
                {!isDOOHCampaign && creative.positions && (
                    <SummaryTypeTextCollection
                        keyName="Positions"
                        values={map(creative.positions, position => positionsMapping[position])}
                    />
                )}
                <SummaryTypeText
                    keyName={useAspectRatio ? 'Aspect Ratio' : 'Size'}
                    value={useAspectRatio ? creative.aspect_ratio : sizesMapping[creative.size]}
                />
                <SummaryTypeText keyName="Type" value={formatsMapping[creative.format]} />
                {isDOOHCampaign && flags.isEnabled('ctef_178_add_new_audio_required_field') && (
                    <SummaryTypeText
                        keyName="Audio"
                        value={creative.audio_required ? 'Required' : 'Not Required'}
                    />
                )}
            </SummaryGroup>
            {creative.format === 'third_party_vast' && (
                <React.Fragment>
                    <SummaryGroup groupName="Hosted VAST">
                        <SummaryTypeComponent
                            keyName="Hosted VAST URL"
                            value={<StyledLink>{creative.hosted_vast_url}</StyledLink>}
                        />
                        <SummaryTypeComponent
                            keyName="Creative Vendor"
                            value={map(creative.creative_vendors, vendor => {
                                return (
                                    <Chip
                                        key={vendor}
                                        label={creativeTrackingVendorsMapping[vendor] || vendor}
                                        variant="outlined"
                                    />
                                );
                            })}
                        />
                    </SummaryGroup>
                    <SummaryGroup groupName="Video">
                        <SummaryTypeText keyName="Duration" value={creative.video_duration} />
                        {creative.video_bit_rate_kbps && (
                            <SummaryTypeText
                                keyName="Bit Rate (Kbps"
                                value={creative.video_bit_rate_kbps}
                            />
                        )}
                        <SummaryTypeText
                            keyName="VPAID"
                            value={creative.capabilities.contains_vpaid ? 'Enabled' : 'Disabled'}
                        />
                    </SummaryGroup>
                </React.Fragment>
            )}
            <SummaryGroup groupName="Creative Asset">
                {(creative.format === 'third_party_vast' ||
                    creative.format === 'mraid' ||
                    creative.format === 'custom_html') && (
                    <SummaryTypeComponent
                        keyName="Reference Image"
                        value={
                            creative.reference_image_preview_url ? (
                                <img
                                    src={creative.reference_image_preview_url}
                                    className={classes.image}
                                />
                            ) : (
                                'None'
                            )
                        }
                    />
                )}
                {creative.reference_screenshot && creative.format === 'third_party_vast' && (
                    <SummaryTypeComponent
                        keyName="Reference Screenshot"
                        value={
                            <img src={creative.reference_screenshot} className={classes.image} />
                        }
                    />
                )}
                {creative.content_html && (
                    <SummaryTypeComponent
                        keyName="HTML Markup"
                        value={
                            <div className="ef3-campaignOverview_CreativeList_expandedSection_rowRight">
                                <PrimaryButton
                                    className="content-html-modal-button"
                                    text="Open Modal"
                                />
                                <ClickToCopyButton
                                    textToCopy={creative.content_html}
                                    textToDisplay="Copy"
                                    buttonClass="ef3-historyTable_copyButton"
                                />

                                <div className={classnames(['content_html'])}>
                                    {creative.content_html}
                                </div>
                            </div>
                        }
                    />
                )}
                {!creative.content_html &&
                    creative.format !== 'third_party_vast' &&
                    creative.format !== 'native' && (
                        <SummaryTypeComponent
                            keyName={creative.format === 'video' ? 'Video' : 'Creative Asset'}
                            value={creativeAsset}
                        />
                    )}
                {creative.companion_preview_url && (
                    <SummaryTypeComponent
                        keyName="Companion Ad"
                        value={
                            <img src={creative.companion_preview_url} className={classes.image} />
                        }
                    />
                )}
                {creative.format === 'native' && (
                    <React.Fragment>
                        <SummaryTypeComponent
                            keyName="Icon Image"
                            value={<img src={creative.icon_image_url} className={classes.image} />}
                        />
                        <SummaryTypeComponent
                            keyName="Main Image"
                            value={<img src={creative.main_image_url} className={classes.image} />}
                        />
                    </React.Fragment>
                )}
                {creative.format !== 'third_party_vast' && (
                    <SummaryTypeComponent
                        keyName="Creative Vendor"
                        value={map(creative.creative_vendors, vendor => {
                            return (
                                <Chip
                                    key={vendor}
                                    label={creativeTrackingVendorsMapping[vendor] || vendor}
                                    variant="outlined"
                                />
                            );
                        })}
                    />
                )}
                {!creative.third_party_vendors && creative.third_party_vendors && (
                    <SummaryTypeComponent
                        keyName="Third-Party Vendors"
                        value={map(creative.third_party_vendors, vendor => {
                            return (
                                <Chip
                                    key={vendor}
                                    label={vendorToPresentationMapping[vendor] || vendor}
                                    variant="outlined"
                                />
                            );
                        })}
                    />
                )}
            </SummaryGroup>
            {creative.format === 'native' && (
                <SummaryGroup groupName="Content">
                    <SummaryTypeText keyName="Title" value={creative.title} />
                    <SummaryTypeText keyName="Main Text" value={creative.text} />
                    <SummaryTypeText keyName="Sponsored By" value={creative.sponsoredBy} />
                    <SummaryTypeText keyName="Call to Action" value={creative.call_to_action} />
                    <SummaryTypeText
                        keyName="Star Rating"
                        value={creative.star_rating ? creative.star_rating : 'None'}
                    />
                </SummaryGroup>
            )}
            {!isDOOHCampaign && (
                <SummaryGroup groupName="Clicktracking">
                    {(creative.format === 'standard' ||
                        creative.format === 'video' ||
                        creative.format === 'native') && (
                        <SummaryTypeComponent
                            keyName="Third-Party Clicktracker"
                            value={
                                creative.third_party_clickthrough ? (
                                    <div>Use Third-Party Clicktracker</div>
                                ) : (
                                    <div>Don't use Third-Party Clicktracker</div>
                                )
                            }
                        />
                    )}
                    {creative.format === 'standard' && creative.third_party_clickthrough && (
                        <SummaryTypeComponent
                            keyName="Third-Party Clicktracker URL"
                            value={<StyledLink>{creative.clickthrough_url}</StyledLink>}
                        />
                    )}
                    {(creative.format === 'mraid' || creative.format === 'custom_html') && (
                        <SummaryTypeComponent
                            keyName="Clicktracking"
                            value={
                                creative.clickthrough_url === '' ? (
                                    <div>
                                        Use Clickthrough URL set by third-party ad server or tag
                                    </div>
                                ) : (
                                    <div>Set or override Clickthrough URL</div>
                                )
                            }
                        />
                    )}
                    {(creative.format === 'mraid' || creative.format === 'custom_html') &&
                        creative.clickthrough_url !== '' && (
                            <SummaryTypeComponent
                                keyName="Clickthrough URL"
                                value={<StyledLink>{creative.clickthrough_url}</StyledLink>}
                            />
                        )}
                    <SummaryTypeComponent
                        keyName="Landing Page URL"
                        value={<StyledLink>{creative.landing_page}</StyledLink>}
                    />
                </SummaryGroup>
            )}
            {!isDOOHCampaign && (
                <SummaryGroup groupName="Third-Party Pixels">
                    {creative.third_party_pixels.length > 0 ? (
                        <SummaryTypeComponent
                            keyName="Third-Party Pixels"
                            value={map(creative.third_party_pixels, (third_party_pixel, index) => (
                                <StyledLink key={index}>{third_party_pixel}</StyledLink>
                            ))}
                        />
                    ) : (
                        <SummaryTypeComponent keyName="Third-Party Pixels" value={'None'} />
                    )}
                </SummaryGroup>
            )}
            {!isDOOHCampaign && (
                <SummaryGroup groupName="Third-Party Javascript URLs">
                    {creative.third_party_javascript_urls &&
                    creative.third_party_javascript_urls.length > 0 ? (
                        <SummaryTypeComponent
                            keyName="Third-Party Javascript URLs"
                            value={map(
                                creative.third_party_javascript_urls,
                                (third_party_javascript_url, index) => (
                                    <StyledLink key={index}>
                                        {third_party_javascript_url}
                                    </StyledLink>
                                )
                            )}
                        />
                    ) : (
                        <SummaryTypeComponent
                            keyName="Third-Party Javascript URLs"
                            value={'None'}
                        />
                    )}
                </SummaryGroup>
            )}
            {!isDOOHCampaign && (
                <SummaryGroup groupName="Tracking Vendors">
                    {creative.tracking_vendors.length > 0 ? (
                        <SummaryTypeComponent
                            keyName="Tracking Vendors"
                            value={map(creative.tracking_vendors, vendor => {
                                return (
                                    <Chip
                                        key={vendor}
                                        label={creativeTrackingVendorsMapping[vendor] || vendor}
                                        variant="outlined"
                                    />
                                );
                            })}
                        />
                    ) : (
                        <SummaryTypeComponent keyName="Tracking Vendors" value={'None'} />
                    )}
                </SummaryGroup>
            )}
        </SummaryLayout>
    );
}

export default VirtualizedCreativesTable;
